import { useEffect, useMemo, useState, type MouseEvent } from 'react';
import { AxiosError } from 'axios';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  Grid,
  Checkbox,
  IconButton,
  InputAdornment,
  Menu,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { Clear, Search } from '@mui/icons-material';
import { useAuth } from '@/auth/AuthProvider';
import PageHeader from '@/components/PageHeader';
import VariboxCreateDialog from '@/components/VariboxCreateDialog';
import VariboxDetailsDialog from '@/components/VariboxDetailsDialog';
import VariboxEditDialog from '@/components/VariboxEditDialog';
import VariboxStatusBadge from '@/components/VariboxStatusBadge';
import {
  type VariboxFilterValue,
  type VariboxStatusKey,
} from '@/data/variboxMockData';
import { useI18n } from '@/i18n';
import { listClients } from '@/services/clientApi';
import { getLocationTree, type ApiLocationNode } from '@/services/locationApi';
import { bulkDeleteVariboxes, deleteVaribox, listVariboxes, mapApiVariboxToView, type VariboxViewRecord } from '@/services/variboxApi';

type FilterState = {
  status: VariboxFilterValue;
  client: string;
  capacity: string;
  sealType: string;
  contract: string;
};

const initialFilters: FilterState = {
  status: '',
  client: '',
  capacity: '',
  sealType: '',
  contract: '',
};

function flattenLocationOptions(nodes: ApiLocationNode[]) {
  const options: Array<{ id: string; label: string }> = [];
  const walk = (items: ApiLocationNode[], depth: number) => {
    for (const item of items) {
      options.push({
        id: item.id,
        label: `${depth ? `${'\u00a0\u00a0'.repeat(depth)}↳ ` : ''}${item.code ? `${item.code} ` : ''}${item.name}`,
      });
      walk(item.children, depth + 1);
    }
  };
  walk(nodes, 0);
  return options;
}

function StatCard({
  label,
  value,
  active,
  onClick,
}: {
  label: string;
  value: number;
  active?: boolean;
  onClick: () => void;
}) {
  return (
    <Card
      sx={{
        border: '1px solid',
        borderColor: active ? 'primary.main' : 'rgba(148,163,184,0.18)',
        boxShadow: active ? '0 16px 32px rgba(20,63,107,0.12)' : 'none',
      }}
    >
      <CardActionArea onClick={onClick} sx={{ height: '100%' }}>
        <CardContent>
          <Stack spacing={0.75}>
            <Typography variant="overline" color={active ? 'primary.main' : 'text.secondary'} sx={{ letterSpacing: 1.2 }}>
              {label}
            </Typography>
            <Typography variant="h4">{value}</Typography>
          </Stack>
        </CardContent>
      </CardActionArea>
    </Card>
  );
}

export default function AssetsPage() {
  const { t } = useI18n();
  const { user } = useAuth();
  const isAdmin = user?.role === 'Admin';
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const includeLocationDescendants = searchParams.get('includeLocationDescendants') === 'true';
  const [searchQuery, setSearchQuery] = useState('');
  const [appliedSearchQuery, setAppliedSearchQuery] = useState('');
  const [filters, setFilters] = useState<FilterState>(initialFilters);
  const [selectedRecord, setSelectedRecord] = useState<VariboxViewRecord | null>(null);
  const [detailsHistoryVisible, setDetailsHistoryVisible] = useState(true);
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [menuRecord, setMenuRecord] = useState<VariboxViewRecord | null>(null);
  const [allRecords, setAllRecords] = useState<VariboxViewRecord[]>([]);
  const [visibleRecords, setVisibleRecords] = useState<VariboxViewRecord[]>([]);
  const [clientOptions, setClientOptions] = useState<Array<{ id: string; name: string }>>([]);
  const [locationOptions, setLocationOptions] = useState<Array<{ id: string; label: string }>>([]);
  const [locationFilterId, setLocationFilterId] = useState('');
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState('');
  const [actionError, setActionError] = useState('');
  const [actionSuccess, setActionSuccess] = useState('');
  const [serverTotal, setServerTotal] = useState(0);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editRecord, setEditRecord] = useState<VariboxViewRecord | null>(null);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const canEditVaribox = user?.role === 'Admin';

  useEffect(() => {
    const timer = window.setTimeout(() => setAppliedSearchQuery(searchQuery.trim()), 300);
    return () => window.clearTimeout(timer);
  }, [searchQuery]);

  const statusLabels = {
    available: t('assets.statuses.available'),
    occupied: t('assets.statuses.occupied'),
    damaged: t('assets.statuses.damaged'),
    repair: t('assets.statuses.repair'),
    cleaning: t('assets.statuses.cleaning'),
    blocked: t('assets.statuses.blocked'),
  } as const;

  const contractLabels = {
    tak: t('assets.contractValues.tak'),
    nie: t('assets.contractValues.nie'),
  } as const;

  const filteredRecords = useMemo(() => {
    return visibleRecords.filter((record) => {
      const matchesCapacity = !filters.capacity || String(record.capacityL) === filters.capacity;
      const matchesSeal = !filters.sealType || record.sealType === filters.sealType;
      const matchesContract = !filters.contract || record.contract === filters.contract;

      return matchesCapacity && matchesSeal && matchesContract;
    });
  }, [filters.capacity, filters.contract, filters.sealType, visibleRecords]);

  const selectedVisibleCount = useMemo(() => {
    if (!filteredRecords.length || !selectedIds.length) {
      return 0;
    }

    const visibleIdSet = new Set(filteredRecords.map((item) => item.id));
    return selectedIds.filter((id) => visibleIdSet.has(id)).length;
  }, [filteredRecords, selectedIds]);

  const allVisibleSelected = filteredRecords.length > 0 && selectedVisibleCount === filteredRecords.length;

  const summaryCounts = useMemo(() => ({
    all: allRecords.length,
    available: allRecords.filter((record) => record.status === 'available').length,
    occupied: allRecords.filter((record) => record.status === 'occupied').length,
    repair: allRecords.filter((record) => record.status === 'repair').length,
    blocked: allRecords.filter((record) => record.status === 'blocked').length,
  }), [allRecords]);

  const capacityOptions = useMemo(() => {
    return Array.from(new Set(allRecords.map((record) => record.capacityL))).sort((a, b) => a - b);
  }, [allRecords]);

  const sealOptions = useMemo(() => {
    return Array.from(new Set(allRecords.map((record) => record.sealType)));
  }, [allRecords]);

  useEffect(() => {
    const nextClientId = searchParams.get('clientId') ?? '';
    const nextLocationId = searchParams.get('locationId') ?? '';

    setFilters((current) => (current.client === nextClientId ? current : { ...current, client: nextClientId }));
    setLocationFilterId((current) => (current === nextLocationId ? current : nextLocationId));

    if (searchParams.get('create') !== '1') {
      return;
    }

    if (isAdmin) {
      setCreateDialogOpen(true);
    }

    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete('create');
    setSearchParams(nextParams, { replace: true });
  }, [isAdmin, searchParams, setSearchParams]);

  useEffect(() => {
    let cancelled = false;

    const loadInitialData = async () => {
      setLoading(true);
      setErrorMessage('');

      try {
        const [clientsResponse, allVariboxesResponse, locationsResponse] = await Promise.all([
          listClients({ limit: 200, offset: 0 }),
          listVariboxes({ limit: 200, offset: 0 }),
          getLocationTree(),
        ]);

        if (cancelled) {
          return;
        }

        setClientOptions(clientsResponse.items.map((client) => ({ id: client.id, name: client.name })));
        setLocationOptions(flattenLocationOptions(locationsResponse.items));
        setAllRecords(allVariboxesResponse.items.map(mapApiVariboxToView));
      } catch {
        if (!cancelled) {
          setErrorMessage(t('assets.apiError'));
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    loadInitialData();

    return () => {
      cancelled = true;
    };
  }, [t]);

  useEffect(() => {
    let cancelled = false;

    const loadFilteredData = async () => {
      setLoading(true);
      setErrorMessage('');

      try {
        const response = await listVariboxes({
          limit: 200,
          offset: 0,
          search: appliedSearchQuery || undefined,
          status: filters.status || undefined,
          clientId: filters.client || undefined,
          locationId: locationFilterId || undefined,
          includeLocationDescendants,
        });

        if (cancelled) {
          return;
        }

        setVisibleRecords(response.items.map(mapApiVariboxToView));
        setServerTotal(response.total);
        setSelectedIds((current) => {
          const allowed = new Set(response.items.map((item) => item.id));
          return current.filter((id) => allowed.has(id));
        });
      } catch {
        if (!cancelled) {
          setErrorMessage(t('assets.apiError'));
          setVisibleRecords([]);
          setServerTotal(0);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    loadFilteredData();

    return () => {
      cancelled = true;
    };
  }, [appliedSearchQuery, filters.client, filters.status, includeLocationDescendants, locationFilterId, t]);

  const updateFilter = <K extends keyof FilterState>(key: K, value: FilterState[K]) => {
    setFilters((current) => ({
      ...current,
      [key]: value,
    }));
  };

  const updateLocationFilter = (locationId: string) => {
    setLocationFilterId(locationId);
    const nextParams = new URLSearchParams(searchParams);
    if (locationId) {
      nextParams.set('locationId', locationId);
      nextParams.set('includeLocationDescendants', 'true');
    } else {
      nextParams.delete('locationId');
      nextParams.delete('includeLocationDescendants');
    }
    setSearchParams(nextParams, { replace: true });
  };

  const editSelectedVaribox = () => {
    if (!canEditVaribox || selectedVisibleCount !== 1) {
      return;
    }

    const selectedRecordToEdit = filteredRecords.find((record) => selectedIds.includes(record.id));
    if (selectedRecordToEdit) {
      setEditRecord(selectedRecordToEdit);
    }
  };

  const openDetails = (record: VariboxViewRecord, showHistory = true) => {
    setSelectedRecord(record);
    setDetailsHistoryVisible(showHistory);
  };

  const closeDetails = () => {
    setSelectedRecord(null);
  };

  const openMenu = (event: MouseEvent<HTMLButtonElement>, record: VariboxViewRecord) => {
    event.stopPropagation();
    setMenuAnchor(event.currentTarget);
    setMenuRecord(record);
  };

  const closeMenu = () => {
    setMenuAnchor(null);
    setMenuRecord(null);
  };

  const handleMenuAction = (action: 'details' | 'history' | 'edit' | 'block') => {
    if (!menuRecord) {
      return;
    }

    if (action === 'edit') {
      if (canEditVaribox) {
        setEditRecord(menuRecord);
      }
      closeMenu();
      return;
    }

    if (action === 'history') {
      openDetails(menuRecord, true);
    } else {
      openDetails(menuRecord, false);
    }

    closeMenu();
  };

  const handleDeleteVaribox = async () => {
    if (!menuRecord?.id) {
      closeMenu();
      return;
    }

    const confirmed = window.confirm(`${t('assets.delete.confirm')} ${menuRecord.serialNumber}?`);
    if (!confirmed) {
      closeMenu();
      return;
    }

    setActionError('');
    setActionSuccess('');
    closeMenu();

    try {
      await deleteVaribox(menuRecord.id);
      setActionSuccess(`${t('assets.delete.success')}: ${menuRecord.serialNumber}`);
      if (selectedRecord?.id === menuRecord.id) {
        setSelectedRecord(null);
      }
      await reloadData();
    } catch (error) {
      let detail = t('assets.delete.error');
      if (error instanceof AxiosError) {
        const apiDetail = error.response?.data?.detail;
        if (typeof apiDetail === 'string' && apiDetail.trim()) {
          detail = apiDetail;
        }
      }
      setActionError(detail);
    }
  };

  const clearFilters = () => {
    setSearchQuery('');
    setAppliedSearchQuery('');
    setFilters(initialFilters);
    setLocationFilterId('');

    if (searchParams.get('clientId') || searchParams.get('locationId') || searchParams.get('includeLocationDescendants')) {
      const nextParams = new URLSearchParams(searchParams);
      nextParams.delete('clientId');
      nextParams.delete('locationId');
      nextParams.delete('includeLocationDescendants');
      setSearchParams(nextParams, { replace: true });
    }
  };

  const toggleRowSelection = (id: string) => {
    setSelectedIds((current) => {
      if (current.includes(id)) {
        return current.filter((item) => item !== id);
      }
      return [...current, id];
    });
  };

  const toggleSelectAllVisible = () => {
    if (allVisibleSelected) {
      setSelectedIds((current) => {
        const visible = new Set(filteredRecords.map((item) => item.id));
        return current.filter((id) => !visible.has(id));
      });
      return;
    }

    setSelectedIds((current) => {
      const merged = new Set(current);
      for (const record of filteredRecords) {
        merged.add(record.id);
      }
      return Array.from(merged);
    });
  };

  const handleBulkDeleteVariboxes = async () => {
    if (!isAdmin || selectedVisibleCount === 0) {
      return;
    }

    const visible = new Set(filteredRecords.map((item) => item.id));
    const targetIds = selectedIds.filter((id) => visible.has(id));
    if (!targetIds.length) {
      return;
    }

    const confirmed = window.confirm(t('assets.delete.bulkConfirm').replace('{count}', String(targetIds.length)));
    if (!confirmed) {
      return;
    }

    setActionError('');
    setActionSuccess('');

    try {
      const result = await bulkDeleteVariboxes(targetIds);
      setSelectedIds((current) => current.filter((id) => !result.deleted_ids.includes(id)));
      setActionSuccess(t('assets.delete.bulkSuccess').replace('{count}', String(result.deleted_count)));
      await reloadData();
    } catch (error) {
      let detail = t('assets.delete.bulkError');
      if (error instanceof AxiosError) {
        const apiDetail = error.response?.data?.detail;
        if (typeof apiDetail === 'string' && apiDetail.trim()) {
          detail = apiDetail;
        }
      }
      setActionError(detail);
    }
  };

  const reloadData = async () => {
    setLoading(true);
    setErrorMessage('');

    try {
      const [clientsResponse, allVariboxesResponse, filteredResponse] = await Promise.all([
        listClients({ limit: 200, offset: 0 }),
        listVariboxes({ limit: 200, offset: 0 }),
        listVariboxes({
          limit: 200,
          offset: 0,
          search: appliedSearchQuery || undefined,
          status: filters.status || undefined,
          clientId: filters.client || undefined,
          locationId: locationFilterId || undefined,
          includeLocationDescendants,
        }),
      ]);

      setClientOptions(clientsResponse.items.map((client) => ({ id: client.id, name: client.name })));
      setAllRecords(allVariboxesResponse.items.map(mapApiVariboxToView));
      setVisibleRecords(filteredResponse.items.map(mapApiVariboxToView));
      setServerTotal(filteredResponse.total);
    } catch {
      setErrorMessage(t('assets.apiError'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Stack spacing={3}>
      <PageHeader
        eyebrow={t('assets.eyebrow')}
        title={t('assets.title')}
        description={t('assets.description')}
        action={
          <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap>
            <Button variant="outlined" onClick={() => navigate('/scan')}>{t('assets.buttons.scanVaribox')}</Button>
            <Button variant="outlined">{t('assets.buttons.export')}</Button>
            {isAdmin ? <Button variant="contained" onClick={() => setCreateDialogOpen(true)}>{t('assets.buttons.addVaribox')}</Button> : null}
          </Stack>
        }
      />

      <Grid container spacing={2}>
        <Grid item xs={12} md={6} lg={4}>
          <TextField
            fullWidth
            label={t('assets.searchLabel')}
            placeholder={t('assets.searchPlaceholder')}
            helperText={t('assets.searchHint')}
            value={searchQuery}
            onChange={(event) => setSearchQuery(event.target.value)}
            InputProps={{
              startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment>,
              endAdornment: searchQuery ? (
                <InputAdornment position="end">
                  <IconButton aria-label={t('assets.buttons.clearSearch')} edge="end" onClick={() => setSearchQuery('')}>
                    <Clear fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ) : undefined,
            }}
          />
        </Grid>
        <Grid item xs={12} md={6} lg={8}>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap justifyContent={{ xs: 'flex-start', md: 'flex-end' }}>
            {isAdmin ? (
              <Button
                variant="contained"
                color="error"
                disabled={selectedVisibleCount === 0}
                onClick={() => void handleBulkDeleteVariboxes()}
              >
                {t('assets.delete.bulkButton').replace('{count}', String(selectedVisibleCount))}
              </Button>
            ) : null}
            {locationFilterId ? (
              <Button
                variant="contained"
                onClick={() => navigate(`/locations/${locationFilterId}/balance`)}
              >
                {t('assets.buttons.clientBalance')}
              </Button>
            ) : null}
            {canEditVaribox && selectedVisibleCount === 1 ? (
              <Button variant="outlined" onClick={editSelectedVaribox}>
                {t('assets.buttons.editSelected')}
              </Button>
            ) : null}
            <Chip label={`${t('assets.searchLabel')}: ${filteredRecords.length}/${serverTotal}`} variant="outlined" />
            <Chip label={`${t('assets.filters.contract')}: ${filters.contract || t('assets.filters.all')}`} variant="outlined" />
            {locationFilterId ? (
              <Chip
                label={`${t('assets.filters.location')}: ${locationOptions.find((location) => location.id === locationFilterId)?.label ?? t('assets.filters.locationFromMap')}`}
                color="primary"
                variant="outlined"
              />
            ) : null}
          </Stack>
        </Grid>
      </Grid>

      <Grid container spacing={2.5}>
        <Grid item xs={12} sm={6} md={4} lg={2.4}>
          <StatCard label={t('assets.stats.all')} value={summaryCounts.all} active={!filters.status} onClick={() => updateFilter('status', '')} />
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={2.4}>
          <StatCard label={t('assets.stats.available')} value={summaryCounts.available} active={filters.status === 'available'} onClick={() => updateFilter('status', 'available')} />
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={2.4}>
          <StatCard label={t('assets.stats.occupied')} value={summaryCounts.occupied} active={filters.status === 'occupied'} onClick={() => updateFilter('status', 'occupied')} />
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={2.4}>
          <StatCard label={t('assets.stats.repair')} value={summaryCounts.repair} active={filters.status === 'repair'} onClick={() => updateFilter('status', 'repair')} />
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={2.4}>
          <StatCard label={t('assets.stats.blocked')} value={summaryCounts.blocked} active={filters.status === 'blocked'} onClick={() => updateFilter('status', 'blocked')} />
        </Grid>
      </Grid>

      <Paper variant="outlined" sx={{ p: 2.5 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4} lg={2}>
            <TextField
              select
              fullWidth
              label={t('assets.filters.status')}
              value={filters.status}
              onChange={(event) => updateFilter('status', event.target.value as VariboxFilterValue)}
            >
              <MenuItem value="">{t('assets.filters.all')}</MenuItem>
              {(Object.keys(statusLabels) as VariboxStatusKey[]).map((status) => (
                <MenuItem key={status} value={status}>{statusLabels[status]}</MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} md={4} lg={2}>
            <Stack spacing={1}>
              <TextField
                select
                fullWidth
                label={t('assets.filters.client')}
                value={filters.client}
                onChange={(event) => updateFilter('client', event.target.value)}
              >
                <MenuItem value="">{t('assets.filters.all')}</MenuItem>
                {clientOptions.map((client) => (
                  <MenuItem key={client.id} value={client.id}>{client.name}</MenuItem>
                ))}
              </TextField>
              {filters.client ? (
                <Button size="small" variant="outlined" onClick={() => navigate(`/clients/${filters.client}/balance`)}>
                  {t('assets.buttons.clientBalance')}
                </Button>
              ) : null}
            </Stack>
          </Grid>
          <Grid item xs={12} md={4} lg={2}>
            <TextField
              select
              fullWidth
              label={t('assets.filters.capacity')}
              value={filters.capacity}
              onChange={(event) => updateFilter('capacity', event.target.value)}
            >
              <MenuItem value="">{t('assets.filters.all')}</MenuItem>
              {capacityOptions.map((capacity) => (
                <MenuItem key={capacity} value={String(capacity)}>{capacity} L</MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} md={4} lg={2}>
            <TextField
              select
              fullWidth
              label={t('assets.filters.sealType')}
              value={filters.sealType}
              onChange={(event) => updateFilter('sealType', event.target.value)}
            >
              <MenuItem value="">{t('assets.filters.all')}</MenuItem>
              {sealOptions.map((sealType) => (
                <MenuItem key={sealType} value={sealType}>{sealType}</MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} md={4} lg={2}>
            <TextField
              select
              fullWidth
              label={t('assets.filters.contract')}
              value={filters.contract}
              onChange={(event) => updateFilter('contract', event.target.value)}
            >
              <MenuItem value="">{t('assets.filters.all')}</MenuItem>
              {(['tak', 'nie'] as const).map((value) => (
                <MenuItem key={value} value={value}>{contractLabels[value]}</MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} md={4} lg={2}>
            <Stack spacing={1}>
              <TextField
                select
                fullWidth
                label={t('assets.filters.location')}
                value={locationFilterId}
                onChange={(event) => updateLocationFilter(event.target.value)}
              >
                <MenuItem value="">{t('assets.filters.all')}</MenuItem>
                {locationOptions.map((location) => (
                  <MenuItem key={location.id} value={location.id}>{location.label}</MenuItem>
                ))}
              </TextField>
              {locationFilterId ? (
                <Button size="small" variant="outlined" onClick={() => navigate(`/locations/${locationFilterId}/balance`)}>
                  {t('assets.buttons.clientBalance')}
                </Button>
              ) : null}
            </Stack>
          </Grid>
          <Grid item xs={12} md={12} lg={12}>
            <Stack direction="row" justifyContent="flex-end">
              <Button variant="text" onClick={clearFilters}>
                {t('assets.buttons.clearFilters')}
              </Button>
            </Stack>
          </Grid>
        </Grid>
      </Paper>

      <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
        {actionSuccess ? (
          <Box sx={{ p: 2, pb: 0 }}>
            <Alert severity="success" onClose={() => setActionSuccess('')}>{actionSuccess}</Alert>
          </Box>
        ) : null}

        {actionError ? (
          <Box sx={{ p: 2, pb: 0 }}>
            <Alert severity="error" onClose={() => setActionError('')}>{actionError}</Alert>
          </Box>
        ) : null}

        {loading ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="body1" color="text.secondary">{t('assets.loadingData')}</Typography>
          </Box>
        ) : null}

        {!loading && errorMessage ? (
          <Box sx={{ p: 3 }}>
            <Alert severity="error" action={<Button color="inherit" size="small" onClick={reloadData}>{t('common.buttons.retry')}</Button>}>
              {errorMessage}
            </Alert>
          </Box>
        ) : null}

        {!loading && !errorMessage && filteredRecords.length > 0 ? (
          <TableContainer sx={{ overflowX: 'auto' }}>
            <Table sx={{ minWidth: 1480 }}>
              <TableHead>
                <TableRow>
                  {isAdmin ? (
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={allVisibleSelected}
                        indeterminate={selectedVisibleCount > 0 && !allVisibleSelected}
                        onChange={toggleSelectAllVisible}
                        inputProps={{ 'aria-label': t('assets.delete.selectAll') }}
                      />
                    </TableCell>
                  ) : null}
                  <TableCell>{t('assets.headings.serialNumber')}</TableCell>
                  <TableCell>{t('assets.headings.radexId')}</TableCell>
                  <TableCell>{t('assets.headings.capacity')}</TableCell>
                  <TableCell>{t('assets.headings.bodyColor')}</TableCell>
                  <TableCell>{t('assets.headings.sealType')}</TableCell>
                  <TableCell>{t('assets.headings.status')}</TableCell>
                  <TableCell>{t('assets.headings.client')}</TableCell>
                  <TableCell>{t('assets.headings.contract')}</TableCell>
                  <TableCell>{t('assets.headings.rfid')}</TableCell>
                  <TableCell align="right">{t('assets.headings.actions')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredRecords.map((record) => (
                  <TableRow key={record.serialNumber} hover sx={{ cursor: 'pointer' }} onClick={() => openDetails(record, true)}>
                    {isAdmin ? (
                      <TableCell padding="checkbox" onClick={(event) => event.stopPropagation()}>
                        <Checkbox
                          checked={selectedIds.includes(record.id)}
                          onChange={() => toggleRowSelection(record.id)}
                          inputProps={{ 'aria-label': t('assets.delete.selectOne') }}
                        />
                      </TableCell>
                    ) : null}
                    <TableCell>
                      <Stack spacing={0.4}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{record.serialNumber}</Typography>
                        <Typography variant="caption" color="text.secondary">{record.lastUpdatedAt}</Typography>
                      </Stack>
                    </TableCell>
                    <TableCell>{record.radexId}</TableCell>
                    <TableCell>{record.capacityL}</TableCell>
                    <TableCell>{record.bodyColor}</TableCell>
                    <TableCell>{record.sealType}</TableCell>
                    <TableCell>
                      <VariboxStatusBadge status={record.status} />
                    </TableCell>
                    <TableCell>{record.client}</TableCell>
                    <TableCell>{contractLabels[record.contract]}</TableCell>
                    <TableCell>{record.rfid}</TableCell>
                    <TableCell align="right" onClick={(event) => event.stopPropagation()}>
                      <Stack direction="row" spacing={1} justifyContent="flex-end">
                        <Button variant="text" size="small" onClick={() => openDetails(record, false)}>
                          {t('assets.menu.details')}
                        </Button>
                        <Button variant="text" size="small" onClick={(event) => openMenu(event as never, record)}>
                          ⋮
                        </Button>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        ) : null}

        {!loading && !errorMessage && filteredRecords.length === 0 ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="h6" sx={{ mb: 1 }}>{t('assets.emptyState.title')}</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2, maxWidth: 620, mx: 'auto' }}>
              {t('assets.emptyState.description')}
            </Typography>
            <Button variant="outlined" onClick={clearFilters}>{t('assets.buttons.clearFilters')}</Button>
          </Box>
        ) : null}
      </Paper>

      <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={closeMenu}>
        <MenuItem onClick={() => handleMenuAction('details')}>{t('assets.menu.details')}</MenuItem>
        <MenuItem onClick={() => handleMenuAction('history')}>{t('assets.menu.history')}</MenuItem>
        {canEditVaribox ? <MenuItem onClick={() => handleMenuAction('edit')}>{t('assets.menu.edit')}</MenuItem> : null}
        <MenuItem onClick={() => handleMenuAction('block')}>{t('assets.menu.block')}</MenuItem>
        {isAdmin ? <MenuItem onClick={() => void handleDeleteVaribox()} sx={{ color: 'error.main' }}>{t('assets.menu.delete')}</MenuItem> : null}
      </Menu>

      <VariboxDetailsDialog
        open={Boolean(selectedRecord)}
        record={selectedRecord}
        onClose={closeDetails}
        onEdit={() => {
          if (canEditVaribox && selectedRecord) {
            setSelectedRecord(null);
            setEditRecord(selectedRecord);
          }
        }}
        defaultHistoryVisible={detailsHistoryVisible}
        onUpdated={reloadData}
      />

      <VariboxCreateDialog
        open={createDialogOpen}
        clientOptions={clientOptions}
        onClose={() => setCreateDialogOpen(false)}
        onCreated={reloadData}
      />

      <VariboxEditDialog
        open={Boolean(editRecord)}
        record={editRecord}
        clientOptions={clientOptions}
        onClose={() => setEditRecord(null)}
        onUpdated={reloadData}
      />
    </Stack>
  );
}